import { useState, type FormEvent } from 'react';
import { errorText } from '../../lib/api/client';
import styles from './LoginScreen.module.css';

interface Props {
  onLogin(email: string, password: string): Promise<void>;
  onSignup(name: string, email: string, password: string): Promise<void>;
  /** Convite: e-mail já definido e explicação do porquê. */
  fixedEmail?: string;
  intro?: string;
}

type Mode = 'login' | 'signup';

export function LoginScreen({ onLogin, onSignup, fixedEmail, intro }: Props) {
  const [mode, setMode] = useState<Mode>(fixedEmail ? 'signup' : 'login');
  const [name, setName] = useState('');
  const [email, setEmail] = useState(fixedEmail ?? '');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function switchMode(next: Mode) {
    setMode(next);
    setError(null);
    setPassword('');
    setConfirm('');
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (mode === 'signup') {
      if (password.length < 8) return setError('A senha precisa ter pelo menos 8 caracteres.');
      if (password !== confirm) return setError('As senhas não conferem.');
    }
    setBusy(true);
    setError(null);
    try {
      if (mode === 'login') await onLogin(email.trim(), password);
      else await onSignup(name.trim(), email.trim(), password);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className={styles.card}>
      <div className={styles.tabs} role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'login'}
          className={`${styles.tab} ${mode === 'login' ? styles.tabActive : ''}`}
          onClick={() => switchMode('login')}
        >
          Entrar
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'signup'}
          className={`${styles.tab} ${mode === 'signup' ? styles.tabActive : ''}`}
          onClick={() => switchMode('signup')}
        >
          Criar conta
        </button>
      </div>

      {intro && <p className={styles.intro}>{intro}</p>}

      <form className={styles.form} onSubmit={submit}>
        {mode === 'signup' && (
          <label className={styles.field}>
            <span className={styles.label}>Nome</span>
            <input
              className={styles.input}
              autoComplete="name"
              autoFocus
              required
              maxLength={80}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </label>
        )}
        <label className={styles.field}>
          <span className={styles.label}>E-mail</span>
          <input
            className={styles.input}
            type="email"
            autoComplete="email"
            autoFocus={mode === 'login'}
            required
            readOnly={!!fixedEmail}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>
        <label className={styles.field}>
          <span className={styles.label}>Senha</span>
          <input
            className={styles.input}
            type="password"
            autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </label>
        {mode === 'signup' && (
          <label className={styles.field}>
            <span className={styles.label}>Confirmar senha</span>
            <input
              className={styles.input}
              type="password"
              autoComplete="new-password"
              required
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
          </label>
        )}
        {error && <p className={styles.error}>{error}</p>}
        <button type="submit" className={styles.primary} disabled={busy}>
          {busy ? 'Aguarde...' : mode === 'login' ? 'Entrar' : 'Criar conta'}
        </button>
      </form>
    </section>
  );
}
